import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';
import { Form, Modal, ModalProps, Upload, message } from 'antd';
import { FaImages } from 'react-icons/fa';
import { useWorkOrderChecklistImageCreate } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import { useWorkorderChecklistContext } from '../WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '../WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { WorkOrderChecklistImagesFormValues } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { compressImage } from '@/utils/functions/compressImage';
import { validateImage } from '@/utils/functions/validateImage';
import { useState } from 'react';

interface Props extends ModalProps {
  onCancel: () => void;
}

const MIME_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

const WorkorderChecklistImagesCreateModal: React.FC<Props> = (props) => {
  const [form] = Form.useForm<WorkOrderChecklistImagesFormValues>();
  const checklistImageCreate = useWorkOrderChecklistImageCreate();
  const { checklistItemId, checklistItemType } = useWorkorderChecklistContext();
  const ruleRequired = useDefaultEmptyFieldError();
  const [loading, setLoading] = useState(false);
  const hasImages = !!Form.useWatch('images', form)?.length;

  function handleCancel() {
    form.resetFields();
    setLoading(false);
    props.onCancel();
  }

  async function handleSubmit() {
    try {
      message.destroy();
      setLoading(true);

      const images = form.getFieldValue('images');

      if (images.length > 30) {
        return message.error('Máximo de 30 imagens');
      }

      const values = await form.validateFields();

      if (!values?.images.length) {
        return message.error('Selecione pelo menos 1 imagem');
      }

      const imagesResponse = await Promise.all(
        values.images.map(async (item) => {
          const compressedFile = await compressImage(
            item.response as unknown as File,
          );

          validateImage(compressedFile);

          return await uploadArchive({
            file: [compressedFile],
            isPublic: false,
          });
        }),
      );

      const filePath = imagesResponse.map((image) => image.filePath);

      if (checklistItemType == WorkorderChecklistItemType.Workorder) {
        await checklistImageCreate.mutateAsync({
          workOrderComplementId: checklistItemId,
          filePath,
        });
      } else if (checklistItemType == WorkorderChecklistItemType.Diagnostic) {
        await checklistImageCreate.mutateAsync({
          diagnosticId: checklistItemId,
          filePath,
        });
      }

      handleCancel();
    } catch {}

    setLoading(false);
  }

  return (
    <Modal
      {...props}
      title="Adicionar imagens"
      onCancel={handleCancel}
      okText="Salvar"
      onOk={handleSubmit}
      okButtonProps={{ disabled: !hasImages }}
      width={500}
      confirmLoading={checklistImageCreate.isLoading || loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="images"
          label="Imagens"
          getValueFromEvent={(e) => {
            const files = Array.isArray(e) ? e : e && e.fileList;

            return files;
          }}
          rules={[
            { type: 'array', min: 1, message: 'Selecione 1 ou mais imagens' },
            { type: 'array', max: 30, message: 'Selecione apenas 30 imagens' },
            ruleRequired,
          ]}
        >
          <Upload.Dragger
            multiple
            style={{ padding: 32 }}
            beforeUpload={(file) => {
              if (!MIME_TYPES.includes(file.type)) {
                message.destroy();
                message.error('Tipo de arquivo inválido, permitido PNG e JPG');
              }

              return MIME_TYPES.includes(file.type) || Upload.LIST_IGNORE;
            }}
            customRequest={({ onSuccess, file }) => {
              if (onSuccess) {
                onSuccess(file);
              }
            }}
            listType="picture"
          >
            <FaImages size={32} />
            <p style={{ marginTop: 10 }}>Arraste ou selecione as imagens</p>
            <p>
              <small>Máximo de 30 imagens</small>
            </p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WorkorderChecklistImagesCreateModal;
